import { useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, Linkedin, Mail, MessageCircle, Phone, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { contact, IMG } from "@/data/vomarcData";
import { Zip } from "./Zip";
import { formatHours, useCallHours } from "./useCallHours";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  space_type: "Luxury home",
  preferred_date: "",
  interest: "Spatial scanning demo",
  message: "",
};

export function ContactSection({ settings = {} }) {
  const [form, setForm] = useState(initialForm);
  const callOpen = useCallHours(settings.call_hours_start, settings.call_hours_end);
  const hoursLabel = formatHours(settings.call_hours_start, settings.call_hours_end);
  const [saving, setSaving] = useState(false);
  const [savedRequest, setSavedRequest] = useState(null);

  const template = useMemo(() => {
    const name = form.name || "VOMARC visitor";
    return `Hello VOMARC team, I am ${name}. I want to book a demo for ${form.space_type}. Interest: ${form.interest}. Preferred slot: ${form.preferred_date || "Please suggest"}. Message: ${form.message || "I want to understand spatial scanning, AI interiors, AR visualization and execution."}`;
  }, [form]);

  const encodedTemplate = encodeURIComponent(template);
  const whatsappUrl = `https://wa.me/91${contact.phone}?text=${encodedTemplate}`;
  const mailUrl = `mailto:${contact.email}?subject=${encodeURIComponent("VOMARC Demo Booking Request")}&body=${encodedTemplate}`;

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submitDemo = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(`${API}/demo-requests`, form);
      setSavedRequest(response.data);
      toast.success("Thank you, your demo request is saved. Our team will be in touch soon.");
      setForm(initialForm);
    } catch {
      toast.error("Please check your details and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="demo" className="section contact-section reveal dark-demo-section" data-testid="contact-demo-section">
      <div className="section-copy narrow">
        <span className="eyebrow" data-testid="contact-section-eyebrow">Book the operating system demo</span>
        <h2 data-testid="contact-section-title">Start with a scan request, consultation, or investor-ready platform walkthrough.</h2>
        <p data-testid="contact-section-copy">Every submission is saved inside the VOMARC model and paired with direct call, WhatsApp, and email actions.</p>
      </div>

      <div className="contact-quick-cta" data-testid="contact-quick-cta">
        {callOpen ? (
          <a className="quick-cta call" href={`tel:+91${contact.phone}`} data-testid="quick-call-link"><Phone size={18} /> Call +91 {contact.phone}</a>
        ) : (
          <span className="quick-cta call-closed" data-testid="quick-call-closed"><Phone size={18} /> Calls {hoursLabel}</span>
        )}
        <a className="quick-cta whatsapp" href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="quick-whatsapp-link"><MessageCircle size={18} /> WhatsApp us</a>
      </div>

      <div className="demo-visual-row" data-testid="demo-visual-row">
        <figure className="demo-visual" data-testid="demo-visual-scan">
          <img src={IMG.scan} alt="VOMARC phone LiDAR 3D scan" loading="lazy" />
          <figcaption><strong>Phone LiDAR scan</strong><span>3D mesh at 96% accuracy</span></figcaption>
        </figure>
        <figure className="demo-visual" data-testid="demo-visual-ar">
          <img src={IMG.arRoom} alt="VOMARC AR design preview" loading="lazy" />
          <figcaption><strong>AR redesign</strong><span>Edit every element live</span></figcaption>
        </figure>
        <figure className="demo-visual" data-testid="demo-visual-fsm">
          <img src={IMG.workforce} alt="VOMARC FSM execution" loading="lazy" />
          <figcaption><strong>FSM execution</strong><span>Verified workforce on site</span></figcaption>
        </figure>
      </div>

      <Zip
        testid="zip-book-demo"
        teaser={null}
        buttonOpen="Open booking form + contact actions"
        buttonClose="Hide booking panel"
        defaultOpen={false}
      >
        <div className="contact-grid">
          <form className="demo-form glass-panel" onSubmit={submitDemo} data-testid="demo-booking-form">
            <div className="form-row">
              <Input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" data-testid="demo-form-name-input" />
              <Input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Email address" data-testid="demo-form-email-input" />
            </div>
            <div className="form-row">
              <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Phone number" data-testid="demo-form-phone-input" />
              <Input value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company / property name" data-testid="demo-form-company-input" />
            </div>
            <div className="form-row">
              <select value={form.space_type} onChange={(e) => update("space_type", e.target.value)} data-testid="demo-form-space-type-select">
                <option>Luxury home</option>
                <option>Office / commercial</option>
                <option>Villa / premium residence</option>
                <option>Architecture project</option>
              </select>
              <Input value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} placeholder="Preferred date/time" data-testid="demo-form-date-input" />
            </div>
            <select value={form.interest} onChange={(e) => update("interest", e.target.value)} data-testid="demo-form-interest-select">
              <option>Spatial scanning demo</option>
              <option>AI interior design studio</option>
              <option>Marketplace + execution workflow</option>
              <option>Investor / partnership walkthrough</option>
            </select>
            <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your space or project" data-testid="demo-form-message-textarea" />
            <Button type="submit" className="primary-cta full" disabled={saving} data-testid="demo-form-submit-button">
              <CalendarDays size={18} /> {saving ? "Saving request..." : "Save & Prepare Demo Request"}
            </Button>
            {savedRequest && (
              <div className="thankyou-panel" data-testid="demo-form-success-panel">
                <strong data-testid="demo-form-success-title">Thank you, {savedRequest.name || "VOMARC visitor"}.</strong>
                <span data-testid="demo-form-success-copy">Your demo request <em>{savedRequest.id.slice(0, 8)}</em> has been received. The VOMARC team will reach out within 24 hours. Meanwhile, use the WhatsApp, email or call actions on the right to talk to the founder directly.</span>
              </div>
            )}
          </form>
          <div className="contact-actions glass-panel" data-testid="contact-actions-panel">
            <h3 data-testid="contact-actions-title">Direct founder contact</h3>
            <p className="contact-template-preview" data-testid="contact-template-preview">{template}</p>
            {callOpen ? (
              <a className="action-link" href={`tel:+91${contact.phone}`} data-testid="contact-call-link"><Phone size={18} /> Call</a>
            ) : (
              <button className="action-link disabled" type="button" disabled data-testid="contact-call-disabled-button"><Phone size={18} /> Calls {hoursLabel}</button>
            )}
            <a className="action-link" href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"><MessageCircle size={18} /> Open WhatsApp</a>
            <a className="action-link" href={mailUrl} data-testid="contact-email-link"><Mail size={18} /> Email {contact.email}</a>
            <div className="contact-note-card" data-testid="contact-note-card">
              <ShoppingBag size={18} />
              <span data-testid="contact-note-text">Demo requests can include scanning, design editing, marketplace and FSM workflow requirements.</span>
            </div>
          </div>
        </div>
      </Zip>

      <div className="founder-direct-wrap" data-testid="founder-direct-wrap">
        <Zip
          testid="zip-founder-direct"
          teaser={<p className="founder-direct-teaser">Skip the form. Open this panel for the founder's personal phone, WhatsApp, email and LinkedIn.</p>}
          buttonOpen="Open direct founder contact"
          buttonClose="Hide direct contact"
          defaultOpen={false}
        >
          <div className="founder-direct-card" data-testid="founder-direct-card">
            <div className="founder-direct-head">
              <strong data-testid="founder-direct-name">Founder · Direct line</strong>
              <span data-testid="founder-direct-sub">For partnerships, press and senior investor calls. Speak to the founder personally.</span>
            </div>
            <div className="founder-direct-actions" data-testid="founder-direct-actions">
              {callOpen ? (
                <a className="action-link" href={`tel:+91${contact.phone}`} data-testid="founder-direct-call"><Phone size={18} /> Call founder</a>
              ) : (
                <button className="action-link disabled" type="button" disabled data-testid="founder-direct-call-disabled"><Phone size={18} /> Calls {hoursLabel}</button>
              )}
              <a className="action-link" href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="founder-direct-whatsapp"><MessageCircle size={18} /> WhatsApp the founder</a>
              <a className="action-link" href={mailUrl} data-testid="founder-direct-email"><Mail size={18} /> Email {contact.email}</a>
              {contact.linkedin && (
                <a className="action-link" href={contact.linkedin} target="_blank" rel="noreferrer" data-testid="founder-direct-linkedin"><Linkedin size={18} /> LinkedIn</a>
              )}
            </div>
          </div>
        </Zip>
      </div>
    </section>
  );
}